import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { GitCompareArrows, ArrowLeft, Check, X as XIcon } from "lucide-react";
import { fetchCompareAnnonces } from "@/lib/api";
import { fmtPrice, withWatermark } from "@/lib/utils";

/** Side-by-side comparison page (up to 3 annonces). */
export default function ComparePage() {
  const [params] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Stable string key avoids re-running the effect on every render — `ids` is
  // a freshly-derived array each render, but its serialised form only changes
  // when the URL query string changes.
  const idsKey = ids.join(",");

  useEffect(() => {
    if (!ids.length) { setLoading(false); return; }
    fetchCompareAnnonces(ids)
      .then((d) => setItems(d.items || []))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  if (loading) {
    return <main className="max-w-7xl mx-auto px-6 py-12 text-center" data-testid="compare-loading">Chargement…</main>;
  }

  if (!items.length) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-12 text-center" data-testid="compare-empty">
        <GitCompareArrows size={40} className="mx-auto text-orange-300 mb-3" />
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Comparateur vide</h1>
        <p className="text-[var(--ink-soft)] mt-2">
          Cliquez sur &quot;Comparer cette annonce&quot; sur jusqu&apos;a 3 fiches pour les voir cote-a-cote.
        </p>
        <Link to="/annonces" className="btn-primary mt-5 inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Parcourir les annonces
        </Link>
      </main>
    );
  }

  const fields = [
    ["Prix", (a) => fmtPrice(a.price, a.price_unit), "text-orange-600 font-extrabold text-lg"],
    ["Reference", (a) => a.ref || "-"],
    ["Categorie", (a) => a.category],
    ["Sous-categorie", (a) => a.subcategory || "-"],
    ["Ville", (a) => a.city],
    ["Quartier", (a) => a.quartier || "-"],
    ["Surface", (a) => a.surface_m2 ? `${a.surface_m2} m²` : "-"],
    ["Prix / m²", (a) => (a.surface_m2 && a.price) ? `${Math.round(a.price / a.surface_m2).toLocaleString("fr-FR")} FCFA/m²` : "-"],
    ["Type de bien", (a) => a.type_bien || "-"],
    ["Statut", (a) => a.statut || "-"],
    ["Etat", (a) => (a.etat_bien || "").replace("_", " ") || "-"],
    ["Vues", (a) => (a.views || 0).toLocaleString("fr-FR")],
    ["Verifiee", (a) => a.verified ? "✓" : "—", "font-bold", true],
  ];

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="compare-page">
      <Link to="/annonces" className="text-sm text-[var(--ink-soft)] inline-flex items-center gap-1.5 mb-3">
        <ArrowLeft size={14} /> Retour
      </Link>
      <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mb-2">
        Comparer {items.length} annonces
      </h1>
      <p className="text-[var(--ink-soft)] mb-6">Cote-a-cote. Cliquez sur le titre pour ouvrir la fiche complete.</p>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] border-collapse" data-testid="compare-table">
          <thead>
            <tr>
              <th className="text-left text-xs uppercase text-[var(--ink-soft)] pb-3 font-bold w-32"></th>
              {items.map((a) => (
                <th key={a.id} className="pb-3 px-3 text-left align-bottom">
                  <Link to={`/annonces/${a.ref || a.id}`} className="block">
                    <img src={withWatermark(a.image)} alt="" className="w-full h-32 object-cover rounded-2xl" />
                    <div className="mt-2 font-bold text-sm leading-tight hover:text-orange-600 line-clamp-2">
                      {a.title}
                    </div>
                  </Link>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fields.map(([label, fn, valCls, boolean]) => (
              <tr key={label} className="border-t border-[var(--line)]">
                <td className="py-2.5 text-xs uppercase font-bold text-[var(--ink-soft)] tracking-wide pr-3">
                  {label}
                </td>
                {items.map((a) => {
                  const val = fn(a);
                  return (
                    <td key={a.id} className={`py-2.5 px-3 text-sm ${valCls || ""}`}>
                      {boolean ? (
                        val === "✓"
                          ? <Check size={16} className="text-emerald-600" />
                          : <XIcon size={16} className="text-gray-300" />
                      ) : val}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
